import React from 'react';
import { Field, FieldProps, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';

/**
 * Campo de la guía ("Campos de texto"): etiqueta siempre visible arriba y en bold, asterisco rojo
 * en los obligatorios, ayuda (texto secundario) o error (rojo, con borde rojo del control) debajo,
 * foco con borde primario + halo (alto contraste: amarillo). Envuelve el `Field` de Fluent v9, así
 * que cualquier control v9 (Input, Textarea, Dropdown, Combobox, Checkbox, Switch, DatePicker...)
 * recibe el `id` / `aria-*` automáticamente.
 */

const CONTROL_SELECTORS = '& .fui-Input, & .fui-Textarea, & .fui-Dropdown, & .fui-Combobox, & .fui-SpinButton, & .fui-Select';

const useGuideFieldStyles = makeStyles({
  root: {
    rowGap: tokens.spacingVerticalXS,
    minWidth: 0,
    // Dropdown y Combobox de Fluent traen min-width: 250px: en una celda más angosta se salían y se
    // superponían con el campo vecino. Dentro de un GuideField el control ocupa su celda y nada más.
    '& .fui-Dropdown, & .fui-Combobox': {
      minWidth: 0,
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    '& .fui-Dropdown__button, & .fui-Combobox__input': {
      minWidth: 0,
      overflowX: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .fui-Label__required': {
      color: 'var(--ichtys-error)',
      fontWeight: tokens.fontWeightBold,
    },
    [CONTROL_SELECTORS]: {
      ...shorthands.borderRadius(tokens.borderRadiusMedium),
    },
    '& .fui-Input:focus-within, & .fui-Textarea:focus-within, & .fui-Dropdown:focus-within, & .fui-Combobox:focus-within, & .fui-SpinButton:focus-within, & .fui-Select:focus-within':
      {
        ...shorthands.borderColor('var(--ichtys-focus)'),
        boxShadow: '0 0 0 2px var(--ichtys-focus-halo)',
      },
  },
  error: {
    [CONTROL_SELECTORS]: {
      ...shorthands.borderColor('var(--ichtys-error)'),
    },
    '& .fui-Field__validationMessage': {
      color: 'var(--ichtys-error)',
    },
  },
  label: {
    fontWeight: tokens.fontWeightBold,
    color: 'var(--ichtys-text)',
  },
  hint: {
    fontSize: '13px',
    color: 'var(--ichtys-text-secondary)',
  },
  hintRow: {
    display: 'flex',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
  },
  counter: {
    marginLeft: 'auto',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
});

export interface GuideFieldProps extends Omit<FieldProps, 'label' | 'hint' | 'validationState' | 'validationMessage'> {
  /** Etiqueta (obligatoria según la guía: siempre visible arriba). */
  label: React.ReactNode;
  /** Ayuda debajo del campo (texto secundario). Si hay error, se muestran los dos. */
  hint?: React.ReactNode;
  /** Mensaje de error: pone el campo en estado de error (borde y texto rojos). */
  error?: React.ReactNode;
  /** Advertencia / éxito para casos puntuales (sin error). */
  validationState?: 'warning' | 'success' | 'none';
  validationMessage?: React.ReactNode;
  /** Contador de caracteres abajo a la derecha ("46 / 500"). */
  counter?: { value: number; max: number };
}

export const GuideField = ({
  label,
  hint,
  error,
  counter,
  validationState,
  validationMessage,
  className,
  ...rest
}: GuideFieldProps) => {
  const styles = useGuideFieldStyles();
  const hasError = error !== undefined && error !== null && error !== false && error !== '';

  const counterNode = counter ? (
    <span className={styles.counter} data-guide-field="counter">
      {counter.value} / {counter.max}
    </span>
  ) : null;

  const hintContent =
    hint !== undefined || counterNode ? (
      counterNode ? (
        <span className={styles.hintRow}>
          {hint !== undefined && <span>{hint}</span>}
          {counterNode}
        </span>
      ) : (
        hint
      )
    ) : undefined;

  return (
    <Field
      {...rest}
      className={mergeClasses('ichtys-field', styles.root, hasError && styles.error, className)}
      label={{ children: label, className: styles.label }}
      hint={hintContent !== undefined ? { children: hintContent, className: styles.hint } : undefined}
      validationState={hasError ? 'error' : validationState ?? 'none'}
      validationMessage={(hasError ? error : validationMessage) as FieldProps['validationMessage']}
    />
  );
};

const useFieldGridStyles = makeStyles({
  root: {
    display: 'grid',
    // Columnas sólo si cada una tiene al menos 240 px; si no, una sola (min() evita desbordar en pantallas muy angostas).
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalL,
    alignItems: 'start',
    '& > *': {
      minWidth: 0,
    },
  },
});

/**
 * Grilla de campos de la guía: campos lado a lado sólo cuando entran (cada columna de 240 px o más,
 * separación 16 px); en un drawer small o en pantallas angostas queda en una columna. Cada hijo es una celda.
 */
export const GuideFieldGrid = ({ children, className, ...rest }: React.HTMLAttributes<HTMLDivElement>) => {
  const styles = useFieldGridStyles();
  return (
    <div {...rest} className={mergeClasses('ichtys-field-grid', styles.root, className)}>
      {children}
    </div>
  );
};

const useHelpTextStyles = makeStyles({
  root: {
    ...shorthands.margin(0),
    fontSize: '13px',
    lineHeight: tokens.lineHeightBase200,
    color: 'var(--ichtys-text-secondary)',
  },
});

/** Texto secundario de la guía (13 Regular, texto secundario) para notas dentro de un drawer o formulario. */
export const GuideHelpText = ({ children, className, ...rest }: React.HTMLAttributes<HTMLParagraphElement>) => {
  const styles = useHelpTextStyles();
  return (
    <p {...rest} className={mergeClasses(styles.root, className)}>
      {children}
    </p>
  );
};

export default GuideField;
